import {
    createContext,
    useContext,
    type ReactNode,
} from 'react'

import type { WorkspaceSession } from '@secureblob/sdk'

interface SecureBlobContextValue {
    session: WorkspaceSession | null
    phase: 'connecting' | 'connected' | 'error' | 'closed'
    error: Error | null
}

const SecureBlobContext = createContext<SecureBlobContextValue | null>(null)

export interface SecureBlobProviderProps {
    /**
     * Pre-created WorkspaceSession. The Provider does NOT manage lifecycle —
     * the caller is responsible for creating and closing the session.
     */
    session: WorkspaceSession | null
    /** Optional error from session creation. */
    error?: Error | null
    children: ReactNode
}

/**
 * SecureBlobProvider — wraps children with SDK session context.
 *
 * Usage:
 * ```tsx
 * const session = await sdk.openWorkspace('my-alias')
 * <SecureBlobProvider session={session}>
 *     <App />
 * </SecureBlobProvider>
 * ```
 */
export function SecureBlobProvider({ session, error, children }: SecureBlobProviderProps) {
    const phase: SecureBlobContextValue['phase'] = error
        ? 'error'
        : session
            ? 'connected'
            : 'connecting'

    return (
        <SecureBlobContext.Provider value={{ session, phase, error: error ?? null }}>
            {children}
        </SecureBlobContext.Provider>
    )
}

/**
 * Access the current workspace session from context.
 *
 * @returns { session, phase, error }
 * @throws if used outside SecureBlobProvider
 */
export function useWorkspace() {
    const ctx = useContext(SecureBlobContext)
    if (!ctx) {
        throw new Error('useWorkspace must be used within a <SecureBlobProvider>')
    }
    return ctx
}
